const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * The calendar date in a given IANA timezone, as YYYY-MM-DD.
 *
 * Briefs are keyed by `brief_date`, which is the client's local date, not UTC.
 * Using `toISOString().slice(0, 10)` instead rolls over at 00:00 UTC, so every
 * US client would ask for tomorrow's brief from late afternoon onward and see
 * "no brief" for the rest of the day.
 */
export function todayInTimezone(timezone: string, now: Date = new Date()): string {
  return formatInTimezone(now, timezone)
}

/** Format a specific instant as the YYYY-MM-DD calendar date seen in `timezone`. */
export function formatInTimezone(date: Date, timezone: string): string {
  try {
    // en-CA renders as YYYY-MM-DD, which is already the shape we store.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date)
  } catch {
    // Unknown/garbage timezone: fall back to UTC rather than throwing, so a bad
    // client record degrades to a wrong-by-hours date instead of a broken app.
    return date.toISOString().slice(0, 10)
  }
}

/** Shift an ISO date (YYYY-MM-DD) by whole days. Used for brief history ranges. */
export function addDays(isoDate: string, days: number): string {
  if (!ISO_DATE.test(isoDate)) throw new Error(`Expected YYYY-MM-DD, got: ${isoDate}`)
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}
